import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import {
  BatchMembershipMemberInput,
  BatchMembershipResult,
  Membership,
  MembershipAccountResult,
  MembershipPlan,
  MembershipPlanInput,
  RegisterMembershipInput
} from '../../@type/membership';
import {
  ARCHIVE_MEMBERSHIP_PLAN,
  BATCH_REGISTER_MEMBERSHIPS,
  CANCEL_MEMBERSHIP,
  CREATE_MEMBERSHIP_PLAN,
  GET_MEMBERSHIP,
  GET_MEMBERSHIP_PLANS,
  GET_MEMBERSHIPS,
  REGISTER_MEMBERSHIP,
  RENEW_MEMBERSHIP,
  REQUEST_MEMBERSHIP_ACCOUNT_CODE,
  UPDATE_MEMBERSHIP_PLAN,
  VERIFY_MEMBERSHIP_ACCOUNT_CODE
} from '../../_apis_/queries/membership';
import { removeTypenameKey } from '../../components/_dashboard/promotion/tools';
import { client } from '../../index';
import { getErrorMessage } from '@/utils/stringHelper';

type MembershipState = {
  memberships: Membership[];
  plans: MembershipPlan[];
  isLoadingMemberships: boolean;
  isLoadingPlans: boolean;
};

const initialState: MembershipState = {
  memberships: [],
  plans: [],
  isLoadingMemberships: false,
  isLoadingPlans: false
};

// Apollo results are read-only; return a mutable copy without __typename
const toPlain = <T>(value: T): T => {
  const copy = cloneDeep(value);
  removeTypenameKey(copy);
  return copy;
};

export const fetchMemberships = createAsyncThunk('membership/fetchMemberships', async (_, { rejectWithValue }) => {
  try {
    const response = await client.query<{ memberships: Membership[] }>({
      query: GET_MEMBERSHIPS,
      fetchPolicy: 'network-only'
    });
    if (!response.data?.memberships) return [];
    return toPlain(response.data.memberships);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchMembershipPlans = createAsyncThunk(
  'membership/fetchMembershipPlans',
  async (includeArchived: boolean, { rejectWithValue }) => {
    try {
      const response = await client.query<{ membershipPlans: MembershipPlan[] }>({
        query: GET_MEMBERSHIP_PLANS,
        variables: { includeArchived },
        fetchPolicy: 'network-only'
      });
      if (!response.data?.membershipPlans) return [];
      return toPlain(response.data.membershipPlans);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchMembership = createAsyncThunk('membership/fetchMembership', async (userId: string, { rejectWithValue }) => {
  try {
    const response = await client.query<{ membership: Membership | null }>({
      query: GET_MEMBERSHIP,
      variables: { userId },
      fetchPolicy: 'network-only'
    });
    return response.data?.membership ? toPlain(response.data.membership) : null;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const saveMembershipPlan = createAsyncThunk(
  'membership/saveMembershipPlan',
  async ({ planId, plan }: { planId?: string; plan: MembershipPlanInput }, { rejectWithValue }) => {
    try {
      if (planId) {
        const response = await client.mutate<{ updateMembershipPlan: MembershipPlan }>({
          mutation: UPDATE_MEMBERSHIP_PLAN,
          variables: { planId, plan }
        });
        if (!response.data?.updateMembershipPlan) return rejectWithValue('No data returned');
        return toPlain(response.data.updateMembershipPlan);
      }
      const response = await client.mutate<{ createMembershipPlan: MembershipPlan }>({
        mutation: CREATE_MEMBERSHIP_PLAN,
        variables: { plan }
      });
      if (!response.data?.createMembershipPlan) return rejectWithValue('No data returned');
      return toPlain(response.data.createMembershipPlan);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const archiveMembershipPlan = createAsyncThunk(
  'membership/archiveMembershipPlan',
  async (planId: string, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ archiveMembershipPlan: MembershipPlan }>({
        mutation: ARCHIVE_MEMBERSHIP_PLAN,
        variables: { planId }
      });
      if (!response.data?.archiveMembershipPlan) return rejectWithValue('No data returned');
      return toPlain(response.data.archiveMembershipPlan);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const requestMembershipAccountCode = createAsyncThunk(
  'membership/requestMembershipAccountCode',
  async ({ email, phone }: { email?: string; phone?: string }, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ requestMembershipAccountCode: MembershipAccountResult }>({
        mutation: REQUEST_MEMBERSHIP_ACCOUNT_CODE,
        variables: { email, phone }
      });
      const result = response.data?.requestMembershipAccountCode;
      if (!result) return rejectWithValue('No data returned');
      if (!result.success) return rejectWithValue(result.message || 'Request failed');
      return toPlain(result);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const verifyMembershipAccountCode = createAsyncThunk(
  'membership/verifyMembershipAccountCode',
  async ({ email, phone, code }: { email?: string; phone?: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ verifyMembershipAccountCode: MembershipAccountResult }>({
        mutation: VERIFY_MEMBERSHIP_ACCOUNT_CODE,
        variables: { email, phone, code }
      });
      const result = response.data?.verifyMembershipAccountCode;
      if (!result) return rejectWithValue('No data returned');
      if (!result.success || !result.uid) return rejectWithValue(result.message || 'Verification failed');
      return toPlain(result);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const registerMembership = createAsyncThunk(
  'membership/registerMembership',
  async (input: RegisterMembershipInput, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ registerMembership: Membership }>({
        mutation: REGISTER_MEMBERSHIP,
        variables: { input }
      });
      if (!response.data?.registerMembership) return rejectWithValue('No data returned');
      return toPlain(response.data.registerMembership);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const renewMembership = createAsyncThunk(
  'membership/renewMembership',
  async ({ userId, planId }: { userId: string; planId?: string }, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ renewMembership: Membership }>({
        mutation: RENEW_MEMBERSHIP,
        variables: { userId, planId }
      });
      if (!response.data?.renewMembership) return rejectWithValue('No data returned');
      return toPlain(response.data.renewMembership);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const cancelMembership = createAsyncThunk('membership/cancelMembership', async (userId: string, { rejectWithValue }) => {
  try {
    const response = await client.mutate<{ cancelMembership: Membership }>({
      mutation: CANCEL_MEMBERSHIP,
      variables: { userId }
    });
    if (!response.data?.cancelMembership) return rejectWithValue('No data returned');
    return toPlain(response.data.cancelMembership);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

/** One chunk of a batch import; rows succeed or fail individually (no verification code). */
export const batchRegisterMemberships = createAsyncThunk(
  'membership/batchRegisterMemberships',
  async ({ planId, members }: { planId: string; members: BatchMembershipMemberInput[] }, { rejectWithValue }) => {
    try {
      const response = await client.mutate<{ batchRegisterMemberships: BatchMembershipResult[] }>({
        mutation: BATCH_REGISTER_MEMBERSHIPS,
        variables: { planId, members }
      });
      if (!response.data?.batchRegisterMemberships) return rejectWithValue('No data returned');
      return toPlain(response.data.batchRegisterMemberships);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const upsertMembership = (state: MembershipState, membership: Membership): void => {
  const index = state.memberships.findIndex((item) => item.userId === membership.userId);
  if (index === -1) {
    state.memberships.unshift(membership);
  } else {
    state.memberships[index] = { ...state.memberships[index], ...membership };
  }
};

const upsertPlan = (state: MembershipState, plan: MembershipPlan): void => {
  const index = state.plans.findIndex((item) => item.planId === plan.planId);
  if (index === -1) {
    state.plans.push(plan);
  } else {
    state.plans[index] = plan;
  }
};

const slice = createSlice({
  name: 'membership',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMemberships.pending, (state) => {
        state.isLoadingMemberships = true;
      })
      .addCase(fetchMemberships.fulfilled, (state, action) => {
        state.memberships = action.payload;
        state.isLoadingMemberships = false;
      })
      .addCase(fetchMemberships.rejected, (state) => {
        state.isLoadingMemberships = false;
      })
      .addCase(fetchMembershipPlans.pending, (state) => {
        state.isLoadingPlans = true;
      })
      .addCase(fetchMembershipPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
        state.isLoadingPlans = false;
      })
      .addCase(fetchMembershipPlans.rejected, (state) => {
        state.isLoadingPlans = false;
      })
      .addCase(saveMembershipPlan.fulfilled, (state, action) => {
        upsertPlan(state, action.payload);
      })
      .addCase(archiveMembershipPlan.fulfilled, (state, action) => {
        upsertPlan(state, action.payload);
      })
      .addCase(registerMembership.fulfilled, (state, action) => {
        upsertMembership(state, action.payload);
      })
      .addCase(renewMembership.fulfilled, (state, action) => {
        upsertMembership(state, action.payload);
      })
      .addCase(cancelMembership.fulfilled, (state, action) => {
        upsertMembership(state, action.payload);
      })
      .addCase(batchRegisterMemberships.fulfilled, (state, action) => {
        action.payload.forEach((result) => {
          if (result.membership) upsertMembership(state, result.membership);
        });
      });
  }
});

// Reducer
export default slice.reducer;
